import { Singleton } from '@visiba/instantiation';
import { newApplicationState, FlowState, IState } from '@modules/state/state';
import shortid from 'shortid';

export enum PopoverType {
	Modal,
}

export interface PopoverSpec<T extends PopoverType> {
	jsx: JSX.Element;
	type: T;
	persistent: boolean;
	overlay: boolean;
	animations: boolean;
	id: string;
}

export interface PopoverStackStateValue<T extends PopoverType> {
	[key: string]: PopoverSpec<T>;
}

interface Stack<T extends PopoverType> {
	readonly state: IState<PopoverStackStateValue<T>>;
	createSpecification(jsx: JSX.Element, persistent: boolean): PopoverSpec<T>;
	injectSpecification(spec: PopoverSpec<PopoverType.Modal>): void;
	remove(): void;
}

/** Stack implementation for modal */
class ModalStack implements Stack<PopoverType.Modal> {
	public readonly state = newApplicationState<PopoverStackStateValue<PopoverType.Modal>>({}, FlowState.ACCESSIBLE);
	public currentActiveModal: keyof PopoverStackStateValue<PopoverType.Modal> | null = null;

	/** Creates a modal specification */
	public createSpecification(jsx: JSX.Element, persistent = false, overlay = true, animations = true): PopoverSpec<PopoverType.Modal> {
		const popover: PopoverSpec<PopoverType.Modal> = {
			jsx,
			persistent,
			overlay,
			animations,
			type: PopoverType.Modal,
			id: shortid(),
		};

		return popover;
	}

	/** Updates the application state with the new spec, replacing current active modal */
	public injectSpecification(spec: PopoverSpec<PopoverType.Modal>): void {
		this.currentActiveModal = spec.id;
		this.state.mutate(() => {
			return {
				[spec.id]: spec,
			};
		});
	}

	/** Removes current active modal and updates the state */
	public remove(): void {
		this.state.mutate((prev) => {
			if (this.currentActiveModal !== null) {
				delete prev[this.currentActiveModal];

				this.currentActiveModal = null;
			}

			return prev;
		});
	}
}

@Singleton()
export class PopoverService {
	public readonly modalStack = new ModalStack();

	// May only be one modal present at once
	public get currentActiveModal(): keyof PopoverStackStateValue<PopoverType.Modal> | null {
		return this.modalStack.currentActiveModal;
	}

	constructor() {
		// Empty
	}
}
